<script>
	/* eslint-disable no-unused-vars */
	// NPM Modules
	import dayjs from 'dayjs';
	// Stores
	import BonusBetStore from '@/Stores/User/BonusBetStore.js';
	import UserStore from '@/Stores/UserStore.js';
	import ACLStore from '@/Stores/ACLStore.js';
	// Partials
	import AddBonus from '@/Partials/User/_AddBonus.svelte';
	// Constants
	import { UserRatingBonusBlacklist, UserRatingName } from '@/Constants/User.js';
	// Components
	import Loading from '@/Components/Loading.svelte';
	import CheckBox from '@/Components/CheckBox.svelte';
	import Pagination from '@/Components/Pagination.svelte';
	import PillMultiSelect from '@/Components/PillMultiSelect.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { getUrlParameter, setUrlParameter, paginationBuilder } from '@/Services/Helpers.js';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import { CurrencyFormat } from '@/Services/Helpers';

	export let user;

	$: links = $BonusBetStore && $BonusBetStore.count() && paginationBuilder(BonusBetStore.getPagination());
	$: filterMeta = $BonusBetStore && buildFilterMeta();
	$: bonusBlacklistedRating = UserRatingBonusBlacklist.includes(user.rating.id);

	let filter = {};
	let pageNo = getUrlParameter('page') || 1;
	let pageSize = 25;
	let loading = getBonusBets();
	let showAddBonus = false;
	let loadingBonuses = [];

	function changePage(e) {
		if (e) {
			pageNo = e.detail.page;
			loading = getBonusBets();
			setUrlParameter('page', pageNo);
		}
	}

	function getBonusBets() {
		return BonusBetStore.getUserBonusBets(
			user.id,
			{
				size: pageSize,
				number: pageNo
			},
			filter
		);
	}

	function buildFilterMeta() {
		let meta = { ...BonusBetStore.getFilterMeta() };
		meta[2] = 'Bonus Refund';
		return meta;
	}

	function updateFilter(e) {
		filter[e.detail.name] = e.detail.checked;
	}

	function applyFilter() {
		loading = getBonusBets();
	}

	function resetFilter() {
		filter = {};
		loading = getBonusBets();
	}

	function getSelected() {
		return filter['type_id'] || [];
	}

	function onChangeBonusDisabled(e) {
		loading = true;
		UserStore.updateBonusDisabled(user.id, !e.target.checked)
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'Bonus updated',
					timeout: 3
				});
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Bonus',
					timeout: 5
				});
			})
			.finally(() => (loading = false));
	}

	function expireBonus(bonus) {
		loadingBonuses = [loadingBonuses, ...bonus.id];
		bonus
			.expireBonus()
			.then(() => {
				notify({
					type: 'success',
					title: user.name,
					message: 'Bonus successfully manually expired',
					timeout: 3
				});
				getBonusBets();
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to manually expire onus',
					timeout: 5
				});
			})
			.finally(() => (loading = false));
	}

	function isExpired(bonus) {
		return dayjs().isAfter(dayjs(bonus.expired_at));
	}
</script>

{#if $ACLStore && ACLStore.granted('bonuses.create')}
	<AddBonus bind:open={showAddBonus} {user} onSave={getBonusBets} />
{/if}

{#await loading}
	<Loading />
{:then finishedLoading}
	{#if $ACLStore && ACLStore.granted('bonuses.read')}
		<div class="p-2 mx-4 bg-gray-100 rounded md:flex md:flex-col">
			<div class="flex items-center px-2 bg-dark-grey text-light">
				<h2 class="flex-1 py-2 text-xl italic font-sans-condensed">Bonus Bets</h2>
				{#if $ACLStore && ACLStore.granted('bonuses.update')}
					<div
						class="mr-4"
						on:click={() =>
							bonusBlacklistedRating
								? notify({
										type: 'warning',
										title: "Can't do that!",
										message: "Can't change this while rating is '" + UserRatingName[user.rating.id] + "'",
										timeout: 5
									})
								: ''}
					>
						<CheckBox
							checked={!user.bonus_disabled}
							disabled={bonusBlacklistedRating}
							text="Allow Bonus Bets"
							on:change={onChangeBonusDisabled}
						/>
					</div>
				{/if}
				{#if $ACLStore && ACLStore.granted('bonuses.create')}
					<button on:click={() => (showAddBonus = true)} class="py-1 btn btn-primary focus:outline-none">
						Add Bonus
					</button>
				{/if}
			</div>
			<div class="flex items-center justify-center p-1 space-x-2 border">
				<PillMultiSelect
					className="min-w-24"
					bgColour="bg-grey"
					name="type_id"
					label="Bonus Type"
					on:change={(e) => updateFilter(e)}
					options={Object.entries(filterMeta)}
					action={false}
					checked={getSelected()}
				/>
				<button on:click={applyFilter} class="h-8 btn btn-black">Apply</button>
				<button on:click={resetFilter} class="h-8 btn btn-muted">Reset</button>
			</div>
			<div class="mt-2 bg-light overflow-x-auto">
				<div class="table w-full">
					<div class="table-header-group">
						<div class="table-row text-sm text-center bg-grey text-light">
							<div class="table-cell py-2 pl-2 text-left">Type</div>
							<div class="table-cell py-2">Awarded</div>
							<div class="table-cell py-2">Balance</div>
							<div class="table-cell px-4 py-2 text-left">Description</div>
							<div class="table-cell py-2">Created</div>
							<div class="table-cell py-2">Expiry</div>
							{#if $ACLStore && ACLStore.granted('bonuses.update')}
								<div class="table-cell" />
							{/if}
						</div>
					</div>
					<div class="table-row-group">
						{#if $BonusBetStore && !$BonusBetStore.count()}
							<div class="table-row text-sm text-center">
								<div class="table-cell px-2 py-2 text-left border-b">No Bonus Bets</div>
							</div>
						{:else}
							{#each $BonusBetStore && $BonusBetStore.sortByDesc('expired_at').all() as bonus (bonus.id)}
								<div class="table-row text-sm text-center">
									<div class="table-cell px-2 py-2 text-left border-b w-56" class:text-gray-600={isExpired(bonus)}>
										{bonus.type.name}
									</div>
									<div class="table-cell px-4 py-2 border-b w-40" class:text-gray-600={isExpired(bonus)}>
										{CurrencyFormat(bonus.amount)}
									</div>
									<div class="table-cell px-4 py-2 border-b w-40" class:text-gray-600={isExpired(bonus)}>
										{CurrencyFormat(bonus.balance)}
									</div>
									<div class="table-cell px-4 py-2 text-left border-b" class:text-gray-600={isExpired(bonus)}>
										{bonus.note || ''}
									</div>
									<div class="table-cell px-4 py-2 border-b w-40" class:text-gray-600={isExpired(bonus)}>
										{dayjs(bonus.created_at).format('D MMM YYYY HH:mm')}
									</div>
									<div class="table-cell px-4 py-2 border-b w-48" class:text-gray-600={isExpired(bonus)}>
										<div class="flex flex-row justify-between items-center">
											<span>{dayjs(bonus.expired_at).format('D MMM YYYY HH:mm')}</span>
											{#if isExpired(bonus)}
												<div class="px-1 py-1 mx-1 ml-1 text-gray-100 bg-danger rounded text-tiny">Expired</div>
											{:else}
												<div class="px-1 py-1 mx-1 ml-1 text-gray-100 bg-green-500 rounded text-tiny">Active</div>
											{/if}
										</div>
									</div>
									{#if $ACLStore && ACLStore.granted('bonuses.update')}
										<div class="table-cell px-4 py-0.5 border-b w-40">
											{#if !isExpired(bonus)}
												<LoadingButton
													on:click={() => {
														expireBonus(bonus);
													}}
													loading={loadingBonuses.includes(bonus.id)}
													class="btn border-2 border-danger hover:bg-danger hover:text-white text-danger p-1 font-semibold"
												>
													Expire Now
												</LoadingButton>
											{/if}
										</div>
									{/if}
								</div>
							{/each}
						{/if}
					</div>
				</div>
			</div>
			{#if $BonusBetStore && $BonusBetStore.count()}
				<Pagination {links} on:click={changePage} className="py-4 bg-gray-100 md:sticky md:bottom-0" />
			{/if}
		</div>
	{/if}
{/await}
